<template>
  <div class="permissions">
    <div class="permissions__col">
      <div class="permissions__col__header">
        <h2>{{ $t('vue2.permissions.roles') }}</h2>
      </div>
      <div class="permissions__col__body">
        <ul>
          <Loader v-if="isLoadingRoles" bg-color="#fff" size="small"></Loader>
          <li class="permissions__roles__role" v-for="role in rolesWithMeta" v-else
              :class="{'permissions__roles__role--is-active': isActiveRole(role)}"
              :key="role.id"
              @click="setActiveRole(role)">
            <div class="role__left">
              <p>{{ role.v_title }}</p>
            </div>
            <div class="role__right">
              <span class="role__number-of-user">
                <span class="icon-user"></span>
              {{ role.user_ids.length }}
              </span>
              <DDropdown class="role-dropdown"
                         v-if="role.name !== 'Administrator' && role.name !== 'Regular user' && role.name !== 'Read only user'">
                <ul slot="list">
                  <li @click="setTheEditRole(role)"><span class="icon-pencil"></span> {{ $t('vue2.common.rename') }}
                  </li>
                  <li v-if="role.user_ids.length > 0"
                      class="d-item-warning d-item-warning--disabled hint--right"
                      :aria-label="$t('vue2.permissions.you_cant_delete_a_role_that_has_users')">
                    <div class="d-item-warning--disabled__inner">
                      <span class="icon-bin"></span>
                      {{ $t('vue2.common.delete') }}
                    </div>
                  </li>
                  <li v-if="role.user_ids.length === 0"
                      @click="setTheDeleteRole(role)"
                      class="d-item-warning">
                    <span class="icon-bin"></span>
                    {{ $t('vue2.common.delete') }}
                  </li>
                </ul>
              </DDropdown>
            </div>
          </li>
          <li class="permissions__roles__role__create" v-if="!isLoadingRoles">
            <button class="btn btn-trans" @click="setIsRoleModal(true)">+ {{
                $t('vue2.permissions.create_new_role')
              }}
            </button>
          </li>
        </ul>
      </div>
    </div>
    <div class="permissions__col">
      <div class="permissions__col__header">
        <h2>{{ $t('vue2.permissions.users') }} <span class="lowercase">{{ activeRole ? activeRole.v_title : '' }}</span>
        </h2>
      </div>
      <div class="permissions__col__body">
        <div class="permissions__col__body__users">
          <div class="input-group">
            <DSelect :items="usersForDSelect"
                     @add="addUserToRole($event)"
                     :itemsSelected="[]"
                     :placeholder="$t('vue2.common.add_user')"
                     :showSelectedUsers="false"></DSelect>
          </div>
          <ul class="permissions__col__body__users__list">
            <virtual-list style="height: 500px; overflow-y: auto;"
                          :estimate-size="40"
                          :keeps="20"
                          :data-key="'id'"
                          :data-sources="usersWithActiveRole"
                          :data-component="itemComponent"
                          :extra-props="{
                            selectableRoles: selectableRoles
                          }"
            />
          </ul>
        </div>
      </div>
    </div>
    <div class="permissions__col">
      <div class="permissions__col__header">
        <h2>{{ $t('vue2.permissions.permissions') }} <span class="lowercase">{{
            activeRole && activeRole.v_title
          }}</span></h2>
      </div>
      <div class="permissions__col__body">
        <p class="role__desc" v-if="getRoleDesc.length > 0">{{ getRoleDesc }}</p>
        <ul class="permissions__permissions">
          <li v-for="permission in activePermissions"
              :key="permission.permission"
              :data-permission="permission.permission"
              class="permissions__permissions__permission"
              @click.stop.prevent="togglePermissionToRole(permission.permission)">
            <p v-if="getIsPermissioni18n(permission.permission)">{{ getIsPermissioni18n(permission.permission) }}</p>
            <p v-else>{{ permission.vTitle }}</p>
            <app-toggle :propOn="isPermissionInActiveRole(permission.permission)">
            </app-toggle>
          </li>
        </ul>
      </div>
    </div>
    <DModal v-if="isRoleModal" :showClose="false">
      <div class="role-modal" slot="insertComponent">
        <div class="role-modal__header">
          <h2>
            <span v-if="!theEditRole && !theDeleteRole">{{ $t('vue2.permissions.create_new_role') }}</span>
            <span v-if="theEditRole && !theDeleteRole">{{ $t('vue2.permissions.rename_role') }}</span>
            <span v-if="theDeleteRole">{{ $t('vue2.permissions.delete_role') }}</span>
          </h2>
          <div class="role-modal__close icon__bg-circle" @click="setIsRoleModal(false)">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <div class="role-modal__body">
          <div class="d-group" v-if="!theDeleteRole">
            <label class="d-label">
              <span v-if="!theEditRole">{{ $t('vue2.permissions.give_role_a_title') }}</span>
              <span v-else>{{ $t('vue2.permissions.choose_new_name') }}</span></label>
            <input type="text"
                   :value="createNewRoleName"
                   @input="onInput"
                   ref="roleCreateInputField"
                   class="box-shadow bg-white animated"
                   :class="{'shake': isSavingWithoutInput}">
          </div>
          <div class="d-group marg-1-top" v-if="!theDeleteRole">
            <label class="d-label">{{ $t('vue2.permissions.select_role_to_base_new_on') }}</label>
            <select v-model="existingRoleToCopyId" class="do-select width-100">
              <option
                v-for="role in rolesToCopy"
                :value="role.id"
                :key="role.id">
                {{ role.v_title }}
              </option>
            </select>
          </div>
          <div v-if="theDeleteRole && theDeleteRole.user_ids.length < 1">
            <h4>{{ $t('vue2.common.are_you_sure_you_want_to_delete') }}
              <span class="text-danger">"{{ theDeleteRole.v_title }}"</span> {{ $t('vue2.permissions.role') }} ?</h4>
          </div>
          <div v-if="theDeleteRole && theDeleteRole.user_ids.length > 0" class="unable-to-delete-warning">
            <h3 class="text-danger">{{ $t('vue2.permissions.you_cant_delete_a_role_that_has_users') }}</h3>
            <h4>{{ $t('vue2.permissions.give_the_following_user_a_new_role') }}</h4>
            <ul class="permissions__col__body__users__list">
              <li v-for="user in usersWithActiveRole" class="permissions__col__body__users__list__user">
                <div>
                  <img :src="user.default_thumb_url"/>
                  <p>{{ user.name }}</p>
                </div>
              </li>
            </ul>
          </div>
          <div class="submit-buttons marg-10-top">
            <button @click="setIsRoleModal(false)" class="marg-5-right">{{ $t('vue2.common.cancel') }}</button>
            <button v-if="!theDeleteRole && !isInputFieldLength"
                    class="submit-button bttn--disabled"
                    @click="onNoInput">
              {{ $t('vue2.common.create') }}
            </button>
            <button class="submit-button" v-if="!theEditRole && isInputFieldLength"
                    @click="createNewRole">{{ $t('vue2.common.create') }}
            </button>
            <button class="submit-button" v-if="theEditRole && isInputFieldLength"
                    @click="updateRoleName">{{ $t('vue2.common.rename') }}
            </button>
            <button v-if="theDeleteRole && theDeleteRole.user_ids.length < 1"
                    @click="deleteRole"
                    class="bttn bttn-danger">
              {{ $t('vue2.common.delete') }}
            </button>
            <button class="bttn bttn--danger bttn--disabled" v-if="theDeleteRole && theDeleteRole.user_ids.length > 0">
              {{ $t('vue2.common.delete') }}
            </button>
          </div>
        </div>
      </div>
    </DModal>
    <div class="bg-overlay" v-if="isRoleModal"></div>
    <PermissionsUserChange :roles="selectableRoles" @set="setRole($event)"></PermissionsUserChange>
  </div>
</template>

<script>
import AppToggle from '../common/AppToggle'
import DDropdown from '../common/DDropdown'
import DModal from '../common/DModal'
import docendo from '../../docendo'
import eventbus from '../../eventbus'
import DSelect from '../DSelect/DSelect'
import PermissionsUserChange from './PermissionsUserChange'
import PermissionsUser from './PermissionsUser'
import virtualList from 'vue-virtual-scroll-list'
import Loader from '../common/Loader'

const I18N = {
  en: {
    administrator: 'Administrator',
    read_only_user: 'Read only user',
    regular_user: 'Regular user'
  },
  da: {
    administrator: 'Administrator',
    read_only_user: 'Skrivebeskyttet bruger',
    regular_user: 'Normal bruger'
  },
  nb: {
    administrator: 'Administrator',
    read_only_user: 'Lesetilgang',
    regular_user: 'Vanlig bruker'
  }
}
export default {
  components: {
    Loader,
    DModal, DDropdown, AppToggle, DSelect, PermissionsUserChange, PermissionsUser, virtualList
  },
  data () {
    return {
      itemComponent: PermissionsUser,
      permissions: [],
      roles: [],
      isLoadingRoles: true,
      users: [],
      activeRoleId: null,
      isRoleModal: false,
      createNewRoleName: '',
      theEditRole: null,
      theDeleteRole: null,
      existingRoleToCopyId: null,
      isSavingWithoutInput: false,
      debounceTimer: null
    }
  },
  computed: {
    isInputFieldLength () {
      return this.createNewRoleName.length > 0
    },
    schoolId () {
      if (this.$route && this.$route.params && this.$route.params.school_id) {
        return this.$route.params.school_id
      }
      return this.$store.getters.schoolId
    },
    locale () {
      return this.$store.getters.locale
    },
    isKindergarten () {
      return this.$store.getters.isKindergarten
    },
    rolesWithMeta () {
      const rolesMap = {
        'Read only user': {v_title: I18N[this.locale].read_only_user, v_sort: 3},
        'Regular user': {v_title: I18N[this.locale].regular_user, v_sort: 2},
        'Administrator': {v_title: I18N[this.locale].administrator, v_sort: 1}
      }

      const roles = this.roles.map(role => {
        const vRoleType = role.permissions.includes('can_manage_users')
          ? 'admin'
          : role.permissions.includes('can_do_most_stuff')
            ? 'normalUser'
            : 'readOnly'

        return {
          ...role,
          ...(rolesMap[role.name] || {v_title: role.name}),
          vRoleType
        }
      })

      const sortedRoles = roles.sort((a, b) => {
        if (a.v_sort && b.v_sort) {
          return a.v_sort - b.v_sort
        } else if (a.v_sort) {
          return -1
        } else if (b.v_sort) {
          return 1
        } else {
          return a.v_title.localeCompare(b.v_title)
        }
      })

      return sortedRoles
    },
    rolesToCopy () {
      return this.rolesWithMeta.filter(role => role.vRoleType !== 'admin')
    },
    activeRole () {
      let idx = this.roles.findIndex(role => role.id === this.activeRoleId)
      return this.roles[idx]
    },
    usersWithActiveRole () {
      if (!this.activeRole) {
        return []
      }
      let users = this.activeRole.user_ids.map(userId => this.allUsersInHash[userId])
      users = users.filter(user => user.name)
      return this.$helpers.sortArrOnName(users)
    },
    allUsersSortedByName () {
      return this.$store.getters.usersSortedOnName
    },
    allUsersInHash () {
      return this.$store.getters.allUsersInHash
    },
    usersForDSelect () {
      return this.activeRole ? this.allUsersSortedByName.filter(user => this.activeRole.user_ids.indexOf(user.id) < 0) : this.allUsersSortedByName
    },
    selectableRoles () {
      return this.rolesWithMeta.filter(role => role.id !== this.activeRole.id)
    },
    activeChangeRoleUserId () {
      return eventbus.activeChangeRoleId
    },
    activePermissions () {
      const normalUserPermissions = [
        'can_see_other_subject_requests',
        'can_edit_subject_requests',
        'can_edit_user_year_notes',
        'can_change_substitution',
        'can_edit_own_presence',
        'can_edit_others_presence',
        'can_see_subjects_distribution_page',
        'can_manage_assigned_hours',
        'can_see_assigned_hours',
        'can_manage_calendars',
        'can_manage_absence_reasons',
        'can_see_own_task_overview',
        'can_see_others_task_overviews',
        'can_manage_tasks'
      ].filter(permission =>
        !(this.isKindergarten && permission === 'can_see_subjects_distribution_page')
      )

      if (!this.activeRole) {
        return []
      }

      let filteredPermissions = []
      const isNormalUser = this.activeRole.permissions.includes('can_do_most_stuff')

      switch (this.activeRole.name) {
        case 'Read only user':
          filteredPermissions = this.permissions.filter(permission =>
            [
              'can_edit_subject_requests',
              'can_edit_own_notes',
              'can_see_other_calendars',
              'can_see_own_task_overview',
              'can_see_others_task_overviews',
              'can_edit_own_presence'
            ].includes(permission)
          )
          break
        case 'Regular user':
          filteredPermissions = this.permissions.filter(permission =>
            normalUserPermissions.includes(permission)
          )
          break
        case 'Administrator':
          filteredPermissions = []
          break
        default:
          if (isNormalUser) {
            filteredPermissions = this.permissions.filter(permission =>
              normalUserPermissions.includes(permission)
            )
          } else {
            filteredPermissions = this.permissions.filter(permission =>
              [
                'can_edit_own_notes',
                'can_see_other_calendars',
                'can_see_own_task_overview',
                'can_see_others_task_overviews',
                'can_edit_own_presence'
              ].includes(permission)
            )
          }
      }

      return filteredPermissions
        .map(permission => ({
          permission,
          vTitle: this.$t('vue2.permissions.' + permission) || permission
        }))
        .sort((a, b) => a.vTitle.localeCompare(b.vTitle))
    },
    getRoleDesc () {
      if (!this.activeRole) {
        return ''
      }
      switch (this.activeRole.name) {
        case 'Administrator':
          return this.$t('vue2.permissions.role_desc_administrator')
        case 'Read only user':
          return this.$t('vue2.permissions.role_desc_read_only_user')
        case 'Regular user':
          return this.$t('vue2.permissions.role_desc_regular_user')
        default:
          return ''
      }
    }
  },
  methods: {
    onInput (evt) {
      const val = evt.target.value
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.createNewRoleName = val
      }, 0)
    },
    getIsPermissioni18n (permission) {
      if (!this.isKindergarten) {
        return false
      }
      const kindergartenI18N = {
        can_see_others_task_overviews: 'Kan se andres årsnormsoversigt',
        can_see_own_task_overview: 'Kan se egen årsnormsoversigt'
      }
      return kindergartenI18N[permission]
    },
    readPermissions () {
      docendo.permissionSetsPermissions(this.schoolId, data => {
        this.permissions = data
      }, failure => {
        console.log('something went wrong', failure)
      })
    },
    readPermissionSets () {
      this.isLoadingRoles = true
      docendo.permissionSetsRead(this.schoolId, data => {
        this.roles = data.sort((a, b) => a.name.localeCompare(b.name))
        this.isLoadingRoles = false
        if (this.roles[0]) {
          this.setActiveRole(this.roles[0])
        }
      }, failure => {
        console.log('something went wrong', failure)
        this.isLoadingRoles = false
      })
    },
    createNewRole () {
      const existingRole = this.rolesToCopy.find(
        role => Number(role.id) === Number(this.existingRoleToCopyId)
      )
      let permissions = []
      if (existingRole) {
        console.log('existingRole', existingRole)
        permissions = existingRole.permissions
      }
      this.isLoadingRoles = true
      docendo.permissionSetsCreate(this.schoolId, this.createNewRoleName, permissions, data => {
        eventbus.toast = {text: this.$t('vue2.permissions.role_created'), 'success': true}
        this.roles.push(data)
        this.isLoadingRoles = false
        this.setActiveRole(data)
      }, failure => {
        eventbus.toast = {text: this.$t('vue2.toast.something_went_wrong'), 'success': false}
        this.isLoadingRoles = false
      })
      this.isRoleModal = false
      this.resetCreateRoleForm()
    },
    updateRoleName () {
      const idx = this.roles.findIndex(role => role.id === this.theEditRole.id)
      docendo.permissionSetsUpdate(this.schoolId, this.theEditRole.id, this.createNewRoleName, this.theEditRole.permissions, data => {
        eventbus.toast = {text: this.$t('vue2.permissions.role_updated'), 'success': true}
        this.$set(this.roles, idx, data)
        this.setActiveRole(data)
      }, failure => {
        eventbus.toast = {text: this.$t('vue2.toast.something_went_wrong'), 'success': false}
      })
      this.resetCreateRoleForm()
      this.isRoleModal = false
    },
    resetCreateRoleForm () {
      this.isSavingWithoutInput = false
      this.theEditRole = null
      this.createNewRoleName = ''
      const defaultRole = this.rolesToCopy.find(role => role.name === 'Read only user')
      this.existingRoleToCopyId = defaultRole ? defaultRole.id : null
    },
    setTheDeleteRole (role) {
      this.theDeleteRole = role
      this.setIsRoleModal(true)
    },
    deleteRole () {
      docendo.permissionSetsDelete(this.schoolId, this.theDeleteRole.id, success => {
        eventbus.toast = {text: this.$t('vue2.permissions.role_deleted'), 'success': true}
        let idx = this.roles.findIndex(role => role.id === this.theDeleteRole.id)
        this.roles.splice(idx, 1)
        this.theDeleteRole = null
        this.setIsRoleModal(false)
        this.setActiveRole(this.roles[0])
      }, failure => {
        eventbus.toast = {text: this.$t('vue2.toast.something_went_wrong'), 'success': false}
      })
    },
    togglePermissionToRole (permission) {
      let rolePermissions = this.activeRole.permissions
      let rolePermissionIdx = rolePermissions.indexOf(permission)
      if (rolePermissionIdx < 0) {
        rolePermissions.push(permission)
      } else {
        rolePermissions.splice(rolePermissionIdx, 1)
      }
      docendo.permissionSetsUpdate(this.schoolId, this.activeRole.id, this.activeRole.name, rolePermissions, data => {
        eventbus.toast = {text: this.$t('vue2.permissions.role_updated'), 'success': true}
        let roleIdx = this.roles.findIndex(role => role.id === data.id)
        this.$set(this.roles, roleIdx, data)
        // this.setActiveRole(data.id)
      }, failure => {
        eventbus.toast = {text: this.$t('vue2.toast.something_went_wrong'), 'success': false}
      })
    },
    isPermissionInActiveRole (permission) {
      return this.activeRole ? this.activeRole.permissions.indexOf(permission) > -1 : false
    },
    setActiveRole (role) {
      this.activeRoleId = role.id
    },
    isActiveRole (role) {
      if (!this.activeRole) {
        return false
      }
      return this.activeRole && this.activeRole.name === role.name
    },
    onNoInput () {
      this.isSavingWithoutInput = true
      setTimeout(_ => {
        this.isSavingWithoutInput = false
      }, 1500)
      this.$refs.roleCreateInputField.focus()
    },
    setIsRoleModal (boolean) {
      if (boolean) {
        this.isRoleModal = true
        setTimeout(_ => {
          if (this.$refs.roleCreateInputField) {
            this.$refs.roleCreateInputField.focus()
          }
        }, 500)
        const defaultRole = this.rolesToCopy.find(role => role.name === 'Read only user')
        this.existingRoleToCopyId = defaultRole ? defaultRole.id : null
      } else {
        this.isRoleModal = false
        this.createNewRoleName = ''
        this.theEditRole = null
        this.theDeleteRole = null
      }
    },
    setTheEditRole (role) {
      this.theEditRole = role
      this.createNewRoleName = role.name
      this.setIsRoleModal(true)
    },
    addUserToRole (user) {
      let roleIdx = this.roles.findIndex(role => role.id === this.activeRole.id)
      docendo.schoolUsersAssignPermissionSet(window.SCHOOL.id, user.id, this.activeRole.id, success => {
        this.removeFromAllRoles(user)
        this.roles[roleIdx].user_ids.push(user.id)
      }, failure => {
        eventbus.toast = {text: this.$t('vue2.toast.something_went_wrong'), 'success': false}
      })
    },
    setRole (roleFromDropdown) {
      let user = {
        name: '',
        id: this.activeChangeRoleUserId
      }
      let roleIdx = this.roles.findIndex(role => role.id === roleFromDropdown.id)
      docendo.schoolUsersAssignPermissionSet(window.SCHOOL.id, this.activeChangeRoleUserId, roleFromDropdown.id, success => {
        this.roles[roleIdx].user_ids.push(this.activeChangeRoleUserId)
        this.removeFromActiveRole(user)
        eventbus.activeChangeRoleId = null
      }, failure => {
        eventbus.toast = {text: this.$t('vue2.toast.something_went_wrong'), 'success': false}
      })
    },
    removeFromAllRoles (user) {
      this.roles.forEach((role, idx) => {
        let userIdx = role.user_ids.indexOf(user.id)
        if (userIdx > -1) {
          this.roles[idx].user_ids.splice(userIdx, 1)
        }
      })
    },
    removeFromActiveRole (user) {
      let roleIdx = this.roles.findIndex(role => role.id === this.activeRoleId)
      let userIdx = this.roles[roleIdx].user_ids.indexOf(user.id)
      if (userIdx > -1) {
        this.roles[roleIdx].user_ids.splice(userIdx, 1)
      }
    }
  },
  mounted () {
    this.$store.dispatch('getUsers')
    this.readPermissions()
    this.readPermissionSets()
  }
}
</script>

<style scoped>
@import '../../assets/css/variables.css';

.permissions {
  display: flex;
  justify-content: space-between;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .07);
  background: #fff;
  min-height: 80vh;
}

.permissions__col {
  border: 1px solid #d8d8d8;
  border-width: 1px 0 1px 1px;
  flex: 1;
}

.permissions__col:last-child {
  border-right: 1px solid #d8d8d8;
}

.permissions__col__header {
  padding: 1rem;
  border-bottom: 1px solid #d8d8d8;
}

.permissions__permissions li {
  display: flex;
  align-items: center;
  margin: 0.5rem;
}

.permissions__roles__role {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  font-weight: bold;
  cursor: pointer;
}

.permissions__roles__role p span.is-green {
  color: #31CD87;
  margin-right: 1rem;
}

.permissions__roles__role > p:last-child {
  display: flex;
  align-items: center;
}

.permissions__roles__role.permissions__roles__role--is-active {
  background-color: aliceblue;
}

.permissions__roles__role__create {
  margin: 1rem;
}

.permissions__roles__role__create > button {
  height: 40px;
  width: 100%;
  text-align: left;
  transition: transform 0.25s;
}

.permissions__roles__role__create > button:active {
  transform: scale(0.95);
}

.role__right {
  display: flex;
  align-items: center;
}


.permissions__col__body__users .input-group {
  border-bottom: 1px solid #d8d8d8;
  padding: 1rem;
}

.unable-to-delete-warning .permissions__col__body__users__list {
  background: #ffffff;
  margin: 1rem 0;
}

.permissions__col__body__users__list {
  margin: 0.5rem 0;
}

.permissions__col__body__users__list__user {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
}

.permissions__col__body__users__list__user:hover {
  background: aliceblue;
}

.permissions__col__body__users__list__user > div {
  display: flex;
  align-items: center;
}

.permissions__col__body__users__list__user > div > img {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  margin-right: 0.5rem;
}

.permissions__col__body__header {
  padding: 1rem;
  border-bottom: 1px solid #d8d8d8;
}

.role-modal {
  width: 400px;
}

.role-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: #ffffff;
  border-bottom: 1px solid #d8d8d8;
}

.role-modal__body {
  padding: 1rem;
  max-height: 500px;
  overflow: auto;
}

.submit-buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.btn.btn-trans:hover {
  color: var(--blue);
}

.d-item-warning--disabled {
  width: 100%;
  cursor: not-allowed;
}

.d-item-warning--disabled__inner {
  opacity: 0.4;
}

.role__left {
  display: flex;
  align-items: center;
}

.role__desc {
  padding: 1rem;
}

.permissions__permissions__permission {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.permissions__permissions__permission:hover {
  background: aliceblue;
}

.d-group .d-label {
  margin-bottom: 0.5rem;
}

</style>



// WEBPACK FOOTER //
// Permissions.vue?1520727b